import React, { createContext, useContext, useEffect, useState } from "react";
import CryptoJS from "crypto-js";

const AuthContext = createContext();

export const initialHelperFunction = {
  to: null,
  exeFunction: () => {},
};

function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}

const AuthProvider = ({ children }) => {
  const defaultRegionId = Number(import.meta.env.VITE_DEFAULT_REGION_ID || 10);
  const defaultRegionName = import.meta.env.VITE_DEFAULT_REGION_NAME || "Madurai";

  const initialState = {
    customer_id: null,
    customer_name: "",
    customer_mobile: "",
    customer_email: "",
    token: "",
    region_id: defaultRegionId,
    region_name: defaultRegionName,
    cartCount: 0,
    PAYMENT_KEY: "",
    geolocation: null,
  };

  const normalizeUserRegion = (userData = {}) => ({
    ...initialState,
    ...userData,
    region_id: userData?.region_id ?? initialState.region_id,
    region_name: userData?.region_name ?? initialState.region_name,
  });

  const secretKey = import.meta.env.VITE_SESSION_STORAGE_DATA_KEY;
  // Encrypt data
  const encryptData = (data) => {
    return CryptoJS.AES.encrypt(JSON.stringify(data), secretKey)?.toString();
  };

  // Decrypt data
  const decryptData = (cipherText) => {
    const bytes = CryptoJS.AES.decrypt(cipherText, secretKey);
    return JSON.parse(bytes.toString(CryptoJS.enc.Utf8));
  };

  // Load initial state from localStorage
  const storedAuthObj = localStorage.getItem("authObj")
    ? decryptData(localStorage.getItem("authObj"))
    : {};
    
  const [user, setUser] = useState(
    normalizeUserRegion(storedAuthObj.user || initialState),
  );
  const [fcm, setFcm] = useState(storedAuthObj.fcm || {});
  const [loginDialogOpen, setLoginDialogOpen] = useState(
    storedAuthObj.loginDialogOpen || false
  );
  const [checkoutData, setCheckoutData] = useState(
    storedAuthObj.checkoutData || {}
  );
  const [searchHistory, setSearchHistory] = useState(
    storedAuthObj.searchHistory || []
  );
  const [newlyCreatedAddressId, setNewlyCreatedAddressId] = useState(
    storedAuthObj.newlyCreatedAddressId || 0
  );
  const [categoryList, setCategoryList] = useState(
    storedAuthObj.categoryList || []
  );
  const [helperFunction, setHelperFunction] = useState(initialHelperFunction);

  const [addressDialogOpen, setAddressDialogOpen] = useState(false);
  const [addressCreateFrom, setAddressCreateFrom] = useState(null);
  const [selectAddressDrawer, setSelectAddressDrawer] = useState(false);

  // Save authObj state to localStorage whenever it changes
  useEffect(() => {
    const authObj = {
      user,
      fcm,
      loginDialogOpen,
      checkoutData,
      searchHistory,
      newlyCreatedAddressId,
      categoryList,
    };
    localStorage.setItem("authObj", encryptData(authObj));
  }, [
    user,
    fcm,
    loginDialogOpen,
    checkoutData,
    searchHistory,
    newlyCreatedAddressId,
    categoryList,
  ]);

  const logout = () => {
    setUser(initialState);
    setFcm({});
    setCheckoutData({});
    setSearchHistory([]);
    setNewlyCreatedAddressId(0);
    setHelperFunction(initialHelperFunction);
    setAddressDialogOpen(false);
    localStorage.removeItem("authObj");
  };

  let authObj = {
    user,
    setUser,
    checkoutData,
    setCheckoutData,
    fcm,
    setFcm,
    logout,
    searchHistory,
    setSearchHistory,
    newlyCreatedAddressId,
    setNewlyCreatedAddressId,
    loginDialogOpen,
    setLoginDialogOpen,
    helperFunction,
    setHelperFunction,
    categoryList,
    setCategoryList,
    addressDialogOpen,
    setAddressDialogOpen,
    addressCreateFrom,
    setAddressCreateFrom,
    selectAddressDrawer,
    setSelectAddressDrawer,
  };

  return (
    <AuthContext.Provider value={authObj}>{children}</AuthContext.Provider>
  );
};

export { AuthProvider, useAuth };
